import { useMemo } from "react";

import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import { Box } from "@mui/material";
import { format } from "date-fns";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import formatDollar from "../../_src_shared/utils/functions/formatDollar";
import { StatusBadge } from "./ClosingCalendarBits";
import { AttentionChips } from "../../components/closingReadiness/ReadinessBits";
import {
  eventNoun,
  pipelineStatusLabel,
} from "../../components/closingReadiness/closingReadinessUtils";
import {
  C,
  MONO,
  dayKey,
  loanAmount,
  loanAmountLabel,
  loanClosingDate,
  loanDisplayName,
  loanId,
  loanPropertyAddress,
  statusMetaForLoan,
  sumLoanAmounts,
} from "./closingCalendarUtils";

const GRID_COLS = "minmax(160px, 1.6fr) 120px 140px minmax(0, 1fr) 24px";

const rowMeta = (loan) =>
  [
    loan?.loanNumber != null ? `#${loan.loanNumber}` : null,
    pipelineStatusLabel(loan),
    eventNoun(loan),
    loan?.originator,
    loanPropertyAddress(loan),
  ]
    .filter(Boolean)
    .join("  ·  ");

// Date-grouped list: a header per closing day (weekday, count, total) followed
// by one row per loan. Dateless loans collapse into a trailing group.
const ClosingListView = ({ loans, onSelectLoan }) => {
  const groups = useMemo(() => {
    const map = new Map();
    (loans || []).forEach((loan) => {
      const d = loanClosingDate(loan);
      const key = d ? dayKey(d) : "none";
      if (!map.has(key)) map.set(key, { key, date: d, loans: [] });
      map.get(key).loans.push(loan);
    });
    const arr = [...map.values()];
    arr.sort((a, b) => {
      if (a.key === "none") return 1;
      if (b.key === "none") return -1;
      return a.date - b.date;
    });
    arr.forEach((g) =>
      g.loans.sort((x, y) => (loanAmount(y) ?? 0) - (loanAmount(x) ?? 0)),
    );
    return arr;
  }, [loans]);

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "12px",
        overflow: "hidden",
        border: `1px solid ${C.line}`,
        background: C.surface,
        boxShadow: "0 1px 2px rgba(20,30,50,.06)",
      }}
    >
      {groups.map((g, gi) => (
        <Box
          key={g.key}
          sx={{ borderBottom: gi < groups.length - 1 ? `1px solid ${C.lineSoft}` : "none" }}
        >
          {/* Group header */}
          <Flx
            ac
            jb
            g={1}
            sx={{
              px: 2.25,
              py: 1.4,
              background: "#fafbfc",
              borderBottom: `1px solid ${C.lineSoft}`,
            }}
          >
            <Txt sx={{ fontSize: 13, fontWeight: 700, color: C.ink }}>
              {g.date ? format(g.date, "EEEE, MMMM d") : "No funding date set"}
              <Box component="span" sx={{ color: C.faint, fontWeight: 500, ml: 1 }}>
                · {g.loans.length} loan{g.loans.length === 1 ? "" : "s"}
              </Box>
            </Txt>
            <Txt sx={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, color: C.sub }}>
              {formatDollar(sumLoanAmounts(g.loans))}
            </Txt>
          </Flx>

          {/* Rows */}
          {g.loans.map((loan, ri) => {
            const s = statusMetaForLoan(loan);
            return (
              <Box
                key={loanId(loan)}
                onClick={() => onSelectLoan(loan)}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr auto", md: GRID_COLS },
                  alignItems: "center",
                  columnGap: 2,
                  rowGap: 0.5,
                  px: 2.25,
                  py: 1.4,
                  cursor: "pointer",
                  borderBottom:
                    ri < g.loans.length - 1 ? `1px solid ${C.lineSoft}` : "none",
                  transition: "background 0.1s",
                  "&:hover": { background: "#f7f9fc" },
                }}
              >
                <Txt
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: C.ink,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                  }}
                >
                  {loanDisplayName(loan)}
                </Txt>

                <Txt
                  sx={{
                    fontFamily: MONO,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: C.ink,
                    textAlign: { xs: "right", md: "right" },
                  }}
                >
                  {loanAmountLabel(loan) || "—"}
                </Txt>

                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <StatusBadge statusKey={s.key} />
                </Box>

                <Txt
                  sx={{
                    display: { xs: "none", md: "block" },
                    fontSize: 12.5,
                    color: C.sub,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {rowMeta(loan)}
                </Txt>

                <Box sx={{ display: { xs: "none", md: "block" }, gridColumn: "1 / -1" }}>
                  <AttentionChips card={loan} size="xs" max={4} />
                </Box>

                <ChevronRightRounded
                  sx={{ fontSize: 18, color: C.faint, display: { xs: "none", md: "block" } }}
                />
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default ClosingListView;
